import { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RemoveIcon from '@mui/icons-material/Remove';

import useBackendApi from '../../../../../hooks/useBackendApi';
import type { RequestBody } from '../../../../../types/httpRequests';
import type { AccountEvent } from '../../../../../types/account';
import { formatDateTime } from '../../../../../utils/dateTime';
import {
  EventCompletionPreviewResponse,
  EventCompletionResponse,
  EventCompletionServicePreview,
  eventCompletionErrorMessage,
  parseEventCompletionPreview,
  parseEventCompletionResponse,
} from './eventCompletionApi';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type EventCompletionSectionProps = {
  event: AccountEvent;
  orgPathName: string;
  accountPathName: string;
  onCompleted: (response: EventCompletionResponse) => Promise<void> | void;
};

type CompletionStep = 'adjust' | 'confirm';

const formattedCompletionTime = (value?: string | null): string | null => {
  return formatDateTime(value, { day: 'numeric', month: 'long' });
};

export default function EventCompletionSection({
  event,
  orgPathName,
  accountPathName,
  onCompleted,
}: EventCompletionSectionProps): JSX.Element {
  const { apiFetchRequest } = useBackendApi();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState<CompletionStep>('adjust');
  const [preview, setPreview] = useState<EventCompletionPreviewResponse | null>(null);
  const [services, setServices] = useState<EventCompletionServicePreview[]>([]);
  const [removedServices, setRemovedServices] = useState<EventCompletionServicePreview[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eventEndpoint = `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(
    accountPathName,
  )}/events/${encodeURIComponent(String(event.acc_event_id))}`;

  const loadPreview = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetchRequest({
        endpoint: `${eventEndpoint}/completion/preview`,
        method: 'GET',
      });
      if (!response || response.isError || response.status !== 200) {
        setError(eventCompletionErrorMessage(response?.body, response?.status));
        return false;
      }
      const parsed = parseEventCompletionPreview(response.body);
      if (!parsed) {
        setError('The completion preview response was invalid. Please try again.');
        return false;
      }
      setPreview(parsed);
      setServices(parsed.services.map((service) => ({ ...service, warnings: [...service.warnings] })));
      setRemovedServices([]);
      return true;
    } catch {
      setError('Unable to load the completion preview. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiFetchRequest, eventEndpoint]);

  const openDialog = useCallback(() => {
    setDialogOpen(true);
    setStep('adjust');
    setPreview(null);
    setServices([]);
    setRemovedServices([]);
    setError(null);
    void loadPreview();
  }, [loadPreview]);

  const closeDialog = useCallback(() => {
    if (submitting) return;
    setDialogOpen(false);
    setError(null);
  }, [submitting]);

  const adjustDoneCount = useCallback(
    (index: number, change: number) => {
      if (submitting) return;
      setServices((current) =>
        current.map((service, serviceIndex) => {
          if (serviceIndex !== index) return service;
          return {
            ...service,
            done_count: Math.max(0, service.done_count + change),
          };
        }),
      );
    },
    [submitting],
  );

  const removeService = useCallback(
    (billingServiceKey: EventCompletionServicePreview['billing_service_key']) => {
      if (submitting) return;
      const service = services.find((item) => item.billing_service_key === billingServiceKey);
      if (!service?.can_remove) return;
      setServices((current) => current.filter((item) => item.billing_service_key !== billingServiceKey));
      setRemovedServices((current) => [...current, service]);
    },
    [services, submitting],
  );

  const restoreService = useCallback(
    (billingServiceKey: EventCompletionServicePreview['billing_service_key']) => {
      if (submitting) return;
      const service = removedServices.find((item) => item.billing_service_key === billingServiceKey);
      if (!service) return;
      setRemovedServices((current) => current.filter((item) => item.billing_service_key !== billingServiceKey));
      setServices((current) => [...current, service]);
    },
    [removedServices, submitting],
  );

  const submitCompletion = useCallback(async () => {
    if (!preview || submitting) return;
    setSubmitting(true);
    setError(null);
    const body: RequestBody = {
      service_requests_version: preview.service_requests_version,
      services: services.map((service) => ({
        billing_service_key: service.billing_service_key,
        done_count: service.done_count,
      })),
      removed_service_keys: removedServices.map((service) => service.billing_service_key),
    };
    try {
      const response = await apiFetchRequest({
        endpoint: `${eventEndpoint}/completion`,
        method: 'POST',
        body,
      });
      if (!response || response.isError || response.status !== 200) {
        const message = eventCompletionErrorMessage(response?.body, response?.status);
        if (response?.status === 409) {
          setStep('adjust');
          await loadPreview();
        }
        setError(message);
        return;
      }
      const parsed = parseEventCompletionResponse(response.body);
      if (!parsed) {
        setError('The completion response was invalid. Refresh the event before trying again.');
        return;
      }
      setDialogOpen(false);
      await onCompleted(parsed);
    } catch {
      setError('Unable to complete the event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [apiFetchRequest, eventEndpoint, loadPreview, onCompleted, preview, removedServices, services, submitting]);

  const isCompleted = event.event_completion_status === 'COMPLETED';
  const completionTime = formattedCompletionTime(event.event_completed_at);

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: { xs: 2, md: 3 } } }}>
        <Box
          sx={{
            alignItems: { xs: 'flex-start', sm: 'center' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
            <CheckCircleOutlineIcon color={isCompleted ? 'success' : 'primary'} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Event Completion
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isCompleted
                  ? `Completed${completionTime ? ` on ${completionTime}` : ''}.`
                  : 'Validate service results and finalize completed counts.'}
              </Typography>
            </Box>
          </Box>
          {isCompleted ? (
            <Chip color="success" label="Completed" size="small" />
          ) : (
            <Button onClick={openDialog} startIcon={<CheckCircleOutlineIcon />} variant="contained">
              Mark Event as Complete
            </Button>
          )}
        </Box>
      </CardContent>

      <Dialog fullWidth maxWidth="md" open={dialogOpen} onClose={closeDialog}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {step === 'adjust' ? 'Review completed services' : 'Confirm event completion'}
        </DialogTitle>
        <DialogContent>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {loading && !preview ? (
            <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 1.5, py: 5 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Validating event results…
              </Typography>
            </Box>
          ) : null}

          {!loading && preview && step === 'adjust' ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Adjust completed counts or remove unused services. Changes are saved only when you confirm completion.
              </Typography>
              {preview.warnings.map((warning) => (
                <Alert key={warning} severity="warning">
                  {warning}
                </Alert>
              ))}
              {!services.length && !removedServices.length ? (
                <Alert severity="info">This event has no services to review. You can still mark it complete.</Alert>
              ) : null}
              {services.length ? (
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Table aria-label="Event completion preview" size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'background.default' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Requested
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Recorded
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Completed
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {services.map((service, index) => {
                        const exceedsRequested = service.done_count > service.requested_count;
                        const differsFromRecorded = service.done_count !== service.recorded_result_count;
                        const otherWarnings = service.warnings.filter(
                          (warning) =>
                            warning !== 'The completed count differs from the recorded result count.' &&
                            warning !== 'The completed count exceeds the requested count.',
                        );
                        return (
                          <TableRow key={service.billing_service_key}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {service.name}
                              </Typography>
                              {otherWarnings.map((warning) => (
                                <Typography key={warning} variant="caption" color="warning.main" display="block">
                                  {warning}
                                </Typography>
                              ))}
                              {differsFromRecorded ? (
                                <Typography variant="caption" color="warning.main" display="block">
                                  The completed count differs from the recorded result count.
                                </Typography>
                              ) : null}
                              {exceedsRequested ? (
                                <Typography variant="caption" color="warning.main" display="block">
                                  The completed count exceeds the requested count.
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell align="right">{service.requested_count}</TableCell>
                            <TableCell align="right">{service.recorded_result_count}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.5 }}>
                                <IconButton
                                  aria-label={`Decrease completed count for ${service.name}`}
                                  disabled={submitting || service.done_count === 0}
                                  onClick={() => adjustDoneCount(index, -1)}
                                  size="small"
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Typography sx={{ minWidth: 28, textAlign: 'center' }}>{service.done_count}</Typography>
                                <IconButton
                                  aria-label={`Increase completed count for ${service.name}`}
                                  disabled={submitting}
                                  onClick={() => adjustDoneCount(index, 1)}
                                  size="small"
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              {service.can_remove ? (
                                <Button
                                  color="error"
                                  disabled={submitting}
                                  onClick={() => removeService(service.billing_service_key)}
                                  size="small"
                                  startIcon={<DeleteOutlineIcon />}
                                >
                                  Remove
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
              {removedServices.map((service) => (
                <Alert
                  action={
                    <Button
                      color="inherit"
                      disabled={submitting}
                      onClick={() => restoreService(service.billing_service_key)}
                      size="small"
                    >
                      Undo
                    </Button>
                  }
                  key={service.billing_service_key}
                  severity="warning"
                >
                  {service.name} will be removed when the event is completed.
                </Alert>
              ))}
            </Stack>
          ) : null}

          {!loading && preview && step === 'confirm' ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Confirm the final completed-service counts and removed services.
              </Typography>
              {services.length ? (
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Table aria-label="Final event completion summary" size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'background.default' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Retained service</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Completed
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {services.map((service) => (
                        <TableRow key={service.billing_service_key}>
                          <TableCell>{service.name}</TableCell>
                          <TableCell align="right">{service.done_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No services will remain on this event.</Alert>
              )}
              {removedServices.length ? (
                <Alert severity="warning">
                  Removed services: {removedServices.map((service) => service.name).join(', ')}
                </Alert>
              ) : null}
              <Alert severity="info">
                Completing the event will create a QuickBooks invoice for accounting review when at least one service is
                completed. It will not be emailed or sent to the customer automatically.
              </Alert>
            </Stack>
          ) : null}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={submitting} onClick={step === 'confirm' ? () => setStep('adjust') : closeDialog}>
            {step === 'confirm' ? 'Back' : 'Cancel'}
          </Button>
          {preview && !loading && step === 'adjust' ? (
            <Button disabled={submitting} onClick={() => setStep('confirm')} variant="contained">
              Review Completion
            </Button>
          ) : null}
          {preview && !loading && step === 'confirm' ? (
            <Button
              disabled={submitting}
              onClick={() => void submitCompletion()}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineIcon />}
              variant="contained"
            >
              {submitting ? 'Completing…' : 'Confirm Completion'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Card>
  );
}
